/**
 * Sync service for managing offline time entry synchronization.
 * Handles background sync when coming back online.
 */

import {
  getPendingTimeEntries,
  updateTimeEntrySyncStatus,
  clearSyncedEntries,
  getDB,
} from './db';
import { syncTimeEntries } from './api';

let isSyncing = false;

/**
 * Sync all pending time entries to the server.
 * Returns counts of synced and failed entries.
 */
export async function syncPendingEntries(): Promise<{
  synced: number;
  failed: number;
}> {
  // Prevent concurrent syncs
  if (isSyncing) {
    return { synced: 0, failed: 0 };
  }

  isSyncing = true;
  let synced = 0;
  let failed = 0;

  try {
    const pending = await getPendingTimeEntries();

    if (pending.length === 0) {
      return { synced: 0, failed: 0 };
    }

    // Mark all as syncing
    for (const entry of pending) {
      await updateTimeEntrySyncStatus(entry.clientEntryId, 'syncing');
    }

    // Call sync API
    const response = await syncTimeEntries({
      entries: pending.map((e) => ({
        clientEntryId: e.clientEntryId,
        taskId: e.taskId,
        startTime: e.startTime,
        endTime: e.endTime,
        deviceTimeOffsetSeconds: e.deviceTimeOffsetSeconds,
      })),
    });

    // Process results
    for (const result of response.data.results) {
      if (result.status === 'created' || result.status === 'duplicate') {
        await updateTimeEntrySyncStatus(result.clientEntryId, 'synced');
        synced++;
      } else if (result.status === 'conflict') {
        // Conflict detected - mark as error for user review
        await updateTimeEntrySyncStatus(
          result.clientEntryId,
          'error',
          'Conflict: Task may have been reassigned'
        );
        failed++;
      }
    }

    // Cleanup synced entries after successful sync
    await clearSyncedEntries();
  } catch (error) {
    // Extract error message for debugging
    const errorMessage =
      error instanceof Error ? error.message : String(error);
    console.error('Sync failed:', errorMessage);

    // Network error - revert syncing entries back to pending.
    // Must query 'syncing' index directly since getPendingTimeEntries() only returns 'pending'.
    const db = await getDB();
    const strandedEntries = await db.getAllFromIndex('timeEntries', 'by-sync-status', 'syncing');
    for (const entry of strandedEntries) {
      await updateTimeEntrySyncStatus(
        entry.clientEntryId,
        'pending',
        `Sync failed: ${errorMessage}`
      );
    }
    failed = strandedEntries.length;
  } finally {
    isSyncing = false;
  }

  return { synced, failed };
}

/**
 * Check if a sync is currently in progress.
 */
export function isSyncInProgress(): boolean {
  return isSyncing;
}
